import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Package,
  MapPin,
  CreditCard,
  Truck,
  CheckCircle,
  Clock,
  XCircle,
  Printer,
  Mail,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { ordersApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

import { resolveMediaUrl } from '../../utils/mediaUrl';
import { formatPrice } from '../../utils/currency';

const AdminOrderDetail = () => {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [dispatching, setDispatching] = useState(false);
  const [booking, setBooking] = useState(false);
  const [confirmRefund, setConfirmRefund] = useState(false);
  const [manualTracking, setManualTracking] = useState('');
  const [savingTracking, setSavingTracking] = useState(false);
  const [refunding, setRefunding] = useState(false);

  useEffect(() => {
    fetchOrder();
  }, [orderNumber]);

  const fetchOrder = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const data = await ordersApi.getOrderByNumber(orderNumber, token);
      setOrder(data);
      setNewStatus(data.status);
      setTrackingNumber(data.trackingNumber || '');
    } catch (error) {
      console.error('Error fetching order:', error);
      toast({
        title: 'Error',
        description: 'Failed to load order details',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTracking = async () => {
    try {
      setSavingTracking(true);
      const token = localStorage.getItem('accessToken');
      await ordersApi.admin.setTracking(order.orderNumber, manualTracking.trim(), token);
      toast({ title: 'Tracking saved', description: 'Order marked shipped; the customer has been e-mailed.' });
      setManualTracking('');
      await fetchOrder();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Could not save', variant: 'destructive' });
    } finally {
      setSavingTracking(false);
    }
  };

  const handleRefund = async () => {
    try {
      setRefunding(true);
      const token = localStorage.getItem('accessToken');
      await ordersApi.admin.refund(order.orderNumber, token);
      toast({ title: 'Refunded', description: 'The customer has been e-mailed.' });
      setConfirmRefund(false);
      await fetchOrder();
    } catch (error) {
      toast({ title: 'Refund failed', description: error.response?.data?.detail || 'Stripe refused the refund', variant: 'destructive' });
    } finally {
      setRefunding(false);
    }
  };

  const handleSchedulePickup = async () => {
    try {
      setBooking(true);
      const token = localStorage.getItem('accessToken');
      const result = await ordersApi.admin.schedulePickup(order.orderNumber, token);
      const h = result.handover || {};
      toast({
        title: h.method === 'pickup' ? 'Pickup scheduled' : 'Pickup not available',
        description: h.method === 'pickup'
          ? `${h.date} ${h.time_from}:00–${h.time_to}:00 · ${h.confirmation || ''}`
          : (h.reason || result.reason),
        variant: h.method === 'pickup' ? undefined : 'destructive',
      });
      await fetchOrder();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Pickup failed', variant: 'destructive' });
    } finally {
      setBooking(false);
    }
  };

  const handleCreateDispatch = async () => {
    try {
      setDispatching(true);
      const token = localStorage.getItem('accessToken');
      const result = await ordersApi.admin.createDispatch(order.orderNumber, token);
      toast({
        title: result.ok ? 'Dispatch created' : 'Dispatch not created',
        description: result.ok
          ? `${result.shipment?.carrier || ''} ${result.shipment?.trackingNumber || ''}`.trim()
          : (result.reason === 'test_payment'
            ? 'This order was paid with a Stripe test card: no real label is bought for it.'
            : (result.error || result.reason)),
        variant: result.ok ? undefined : 'destructive',
      });
      await fetchOrder();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Dispatch failed', variant: 'destructive' });
    } finally {
      setDispatching(false);
    }
  };

  const handleUpdateStatus = async () => {
    try {
      setUpdating(true);
      const token = localStorage.getItem('accessToken');
      await ordersApi.admin.updateStatus(order.id, newStatus, trackingNumber, token);
      toast({
        title: 'Success',
        description: 'Order status updated successfully',
      });
      await fetchOrder();
    } catch (error) {
      console.error('Error updating order:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update order status',
        variant: 'destructive',
      });
    } finally {
      setUpdating(false);
    }
  };

  const handlePrintInvoice = () => {
    toast({
      title: 'Print Invoice',
      description: 'Invoice printing feature coming soon',
    });
  };

  const handleSendEmail = () => {
    toast({
      title: 'Send Email',
      description: 'Email notification sent to customer',
    });
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: Clock,
      processing: Package,
      shipped: Truck,
      delivered: CheckCircle,
      cancelled: XCircle,
    };
    return icons[status] || Clock;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-yellow-600 bg-yellow-50',
      processing: 'text-blue-600 bg-blue-50',
      shipped: 'text-purple-600 bg-purple-50',
      delivered: 'text-green-600 bg-green-50',
      cancelled: 'text-red-600 bg-red-50',
    };
    return colors[status] || 'text-gray-600 bg-gray-50';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#be1f1a]"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 mb-4">Order not found</p>
        <Button onClick={() => navigate('/admin/orders')}>Back to Orders</Button>
      </div>
    );
  }

  const StatusIcon = getStatusIcon(order.status);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin/orders')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Order #{order.orderNumber}</h1>
            <p className="text-gray-600">
              Placed on {new Date(order.createdAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })} at {new Date(order.createdAt).toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePrintInvoice}>
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
          <Button variant="outline" onClick={handleSendEmail}>
            <Mail className="h-4 w-4 mr-2" />
            Email
          </Button>
        </div>
      </div>

      {/* Status Update */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Update Order Status</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <Label>Order Status</Label>
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="shipped">Shipped</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Tracking Number</Label>
            <Input
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              placeholder="Enter tracking number"
            />
          </div>
          <div className="flex items-end">
            <Button
              onClick={handleUpdateStatus}
              disabled={updating || newStatus === order.status}
              className="bg-[#be1f1a] hover:bg-[#a71b17] w-full"
            >
              {updating ? 'Updating...' : 'Update Status'}
            </Button>
          </div>
        </div>
      </div>

      {/* Envía dispatch */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Truck className="h-5 w-5" />
            Dispatch
          </h3>
          {!order.shipmentAdmin?.tracking_number && order.paymentStatus === 'paid' && order.shipmentAdmin?.provider !== 'ems' && order.shippingMethod?.id !== 'pickup' && (
            <Button
              onClick={handleCreateDispatch}
              disabled={dispatching}
              className="bg-[#be1f1a] hover:bg-[#a71b17]"
            >
              {dispatching ? 'Creating…' : (order.shipmentAdmin?.status === 'generation_failed' ? 'Retry dispatch' : 'Create dispatch')}
            </Button>
          )}
        </div>
        {order.shipmentAdmin?.tracking_number ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 text-sm">
            <div>
              <p className="text-gray-600">Carrier / service</p>
              <p className="font-medium">{order.shipmentAdmin.service_name || `${order.shipmentAdmin.carrier} ${order.shipmentAdmin.service}`}</p>
              <p className="text-xs text-gray-500">
                {order.shipmentAdmin.chosen_by === 'cheapest' ? 'Cheapest rate (free shipping)' : 'Chosen by the customer'}
              </p>
            </div>
            <div>
              <p className="text-gray-600">Tracking number</p>
              <p className="font-mono font-medium break-all">{order.shipmentAdmin.tracking_number}</p>
              {order.shipmentAdmin.track_url && (
                <a href={order.shipmentAdmin.track_url} target="_blank" rel="noopener noreferrer" className="text-[#be1f1a] hover:underline">Track</a>
              )}
            </div>
            <div>
              <p className="text-gray-600">Carrier status</p>
              <p className="font-medium">{order.shipmentAdmin.status || '—'}</p>
              {order.shipmentAdmin.last_event_at && (
                <p className="text-xs text-gray-500">{new Date(order.shipmentAdmin.last_event_at).toLocaleString()}</p>
              )}
            </div>
            <div>
              <p className="text-gray-600">Cost to the store (internal)</p>
              <p className="font-medium">
                {order.shipmentAdmin.total_price != null ? `${order.shipmentAdmin.total_price} ${order.shipmentAdmin.currency || ''}` : '—'}
              </p>
              {order.shipmentAdmin.label_url && (
                <a href={order.shipmentAdmin.label_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#be1f1a] hover:underline">
                  <Printer className="h-4 w-4" /> Print label
                </a>
              )}
            </div>
          </div>
        ) : null}
        {order.shipmentAdmin?.tracking_number && (() => {
          const h = order.shipmentAdmin.handover;
          const done = h?.status === 'collected' || h?.status === 'dropped_off';
          return (
            <div className="mt-6 border-t pt-4" data-testid="handover-panel">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="text-sm text-gray-600">Handover to the carrier</p>
                  <p className="font-semibold">
                    {!h ? 'Not arranged yet'
                      : h.method === 'pickup' ? `Pickup — ${h.status === 'collected' ? 'collected' : 'scheduled'}`
                      : `Drop-off — ${h.status === 'dropped_off' ? 'dropped off' : 'take the package to a branch'}`}
                  </p>
                </div>
                {(!h || (h.method === 'dropoff' && !done)) && (
                  <Button onClick={handleSchedulePickup} disabled={booking} variant="outline">
                    {booking ? 'Booking…' : 'Schedule pickup'}
                  </Button>
                )}
              </div>
              {h?.method === 'pickup' && (
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-3 text-sm">
                  <div><p className="text-gray-600">Date</p><p className="font-medium">{h.date}</p></div>
                  <div><p className="text-gray-600">Window</p><p className="font-medium">{h.time_from}:00 – {h.time_to}:00</p></div>
                  <div><p className="text-gray-600">Confirmation</p><p className="font-mono font-medium">{h.confirmation || '—'}</p></div>
                  <div><p className="text-gray-600">Pickup fee</p><p className="font-medium">{h.fee != null ? h.fee : '—'}</p></div>
                  <div className="sm:col-span-2"><p className="text-gray-600">Pickup address</p><p className="font-medium">{h.address}</p></div>
                  {h.instructions && <div className="sm:col-span-2"><p className="text-gray-600">Driver instructions</p><p className="font-medium">{h.instructions}</p></div>}
                </div>
              )}
              {h?.method === 'dropoff' && (
                <div className="mt-3 text-sm">
                  {h.reason && <p className="text-amber-700 mb-3">{h.reason}</p>}
                  {(h.branches || []).length > 0 ? (
                    <ul className="space-y-3">
                      {h.branches.map((b) => (
                        <li key={b.code || b.address} className="border rounded-md p-3">
                          <p className="font-medium">{h.carrier} · {b.name || b.code}</p>
                          <p>{b.address}{b.city ? `, ${b.city}` : ''}</p>
                          <p className="text-xs text-gray-500">
                            {b.distance_km != null && `${b.distance_km} km from the origin · `}
                            {(b.hours || []).length ? b.hours.join(' · ') : 'Opening hours not provided by the carrier'}
                          </p>
                          {b.maps_url && (
                            <a href={b.maps_url} target="_blank" rel="noopener noreferrer" className="text-[#be1f1a] hover:underline">Open in Google Maps</a>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>Envía has no branch list for {h.carrier} near the origin: take the package to any {h.carrier} office with the printed label.</p>
                  )}
                </div>
              )}
            </div>
          );
        })()}
        {order.shippingMethod?.id === 'pickup' && (
          <p className="mt-3 text-sm text-gray-700" data-testid="pickup-order-note">
            <strong>Store pickup.</strong> The customer collects this order: there is no shipment, label or carrier pickup.
            Get it ready, contact the customer, and mark the order as Delivered when they collect it.
          </p>
        )}
        {order.shipmentAdmin?.provider === 'ems' && !order.shipmentAdmin?.tracking_number && (
          <div className="mt-4 space-y-3 text-sm" data-testid="ems-dropoff">
            <p className="font-semibold">4-72 EMS — take the package to a 4-72 point (or request corporate pickup)</p>
            <p className="text-gray-600">
              Zone {order.shipmentAdmin.zone || '—'} · chargeable {order.shipmentAdmin.chargeable_kg || '—'} kg ·
              4-72 price {order.shipmentAdmin.total_price != null ? `${order.shipmentAdmin.total_price} USD` : '—'}
            </p>
            {(order.shipmentAdmin.handover?.documents || []).length > 0 && (
              <div>
                <p className="text-gray-600">Bring:</p>
                <ul className="list-disc ml-5">
                  {order.shipmentAdmin.handover.documents.map((d) => <li key={d}>{d}</li>)}
                </ul>
              </div>
            )}
            <div className="flex gap-2 items-end flex-wrap">
              <div>
                <Label>4-72 tracking number</Label>
                <Input value={manualTracking} onChange={(e) => setManualTracking(e.target.value)} placeholder="e.g. EE123456789CO" />
              </div>
              <Button onClick={handleSaveTracking} disabled={savingTracking || !manualTracking.trim()} className="bg-[#be1f1a] hover:bg-[#a71b17]">
                {savingTracking ? 'Saving…' : 'Save tracking & notify customer'}
              </Button>
            </div>
          </div>
        )}
        {!order.shipmentAdmin?.tracking_number && order.shipmentAdmin?.provider !== 'ems' && order.shippingMethod?.id !== 'pickup' && (order.shipmentAdmin?.status === 'generation_failed' ? (
          <p className="mt-3 text-sm text-red-700">
            Envía could not create the dispatch (attempt {order.shipmentAdmin.attempts || 1}): {order.shipmentAdmin.error}.
            It is retried automatically every hour.
          </p>
        ) : (
          <p className="mt-3 text-sm text-gray-600">
            {order.paymentStatus === 'paid'
              ? 'No dispatch yet — it is created automatically after payment (retried every hour).'
              : 'The dispatch is created once the payment is confirmed.'}
          </p>
        ))}
      </div>

      {/* Order Info Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Order Items */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Package className="h-5 w-5" />
            Order Items
          </h3>
          <div className="space-y-4">
            {order.items?.map((item, index) => (
              <div key={index} className="flex gap-4 pb-4 border-b last:border-b-0">
                <img
                  src={resolveMediaUrl(item.image) || 'https://placehold.co/60x80'}
                  alt={item.name?.en}
                  className="w-16 h-20 object-cover rounded"
                  onError={(e) => { e.currentTarget.src = 'https://placehold.co/60x80?text=No+image'; }}
                />
                <div className="flex-1">
                  <p className="font-medium">{item.name?.en || item.name}</p>
                  <p className="text-sm text-gray-500">SKU: {item.sku}</p>
                  <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold">
                    {formatPrice(item.total || 0, item.currency || order.currency || 'COP')}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="mt-6 pt-6 border-t space-y-2">
            <div className="flex justify-between text-sm">
              <span>Subtotal:</span>
              <span>
                {formatPrice(order.subtotal || 0, order.currency || 'COP')}
              </span>
            </div>
            
            {/* Display discount (new format) */}
            {order.discount_amount > 0 && (
              <div className="flex justify-between text-sm text-green-600">
                <span>Discount:</span>
                <span>
                  -{formatPrice(order.discount_amount, order.currency || 'COP')}
                </span>
              </div>
            )}
            
            {/* Display discount (old format) */}
            {order.discount > 0 && !order.discount_amount && (
              <div className="flex justify-between text-sm">
                <span>Discount:</span>
                <span className="text-green-600">
                  -{formatPrice(order.discount || 0, order.currency || 'COP')}
                </span>
              </div>
            )}
            
            {/* Display detailed tax info if available (new format) */}
            {order.tax_info && order.tax_info.tax_amount > 0 && (
              <div className="flex justify-between text-sm">
                <span>
                  {order.tax_info.tax_name} ({order.tax_info.tax_rate}%):
                  {order.tax_info.included_in_price && (
                    <span className="text-xs ml-1">(included)</span>
                  )}
                </span>
                <span>
                  {order.tax_info.included_in_price ? (
                    <span className="text-gray-500">Included</span>
                  ) : (
                    formatPrice(order.tax_info.tax_amount, order.currency || 'COP')
                  )}
                </span>
              </div>
            )}
            
            {/* Display simple tax if no detailed info (old format) */}
            {!order.tax_info && order.tax > 0 && (
              <div className="flex justify-between text-sm">
                <span>Tax:</span>
                <span>
                  {formatPrice(order.tax || 0, order.currency || 'COP')}
                </span>
              </div>
            )}
            
            <div className="flex justify-between text-sm">
              <span>Shipping:</span>
              <span>
                {order.shippingCost === 0 ? 'FREE' : 
                  formatPrice(order.shippingCost || 0, order.currency || 'COP')
                }
              </span>
            </div>
            <div className="flex justify-between text-lg font-semibold pt-2 border-t">
              <span>Total:</span>
              <span className="text-[#be1f1a]">
                {formatPrice(order.total || 0, order.currency || 'COP')}
              </span>
            </div>
          </div>
        </div>

        {/* Customer & Addresses */}
        <div className="space-y-6">
          {/* Customer Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Customer Information</h3>
            <div className="space-y-2 text-sm">
              <p><span className="text-gray-600">Email:</span> <span className="font-medium">{order.email}</span></p>
              <p><span className="text-gray-600">Phone:</span> <span className="font-medium">{order.shippingAddress?.phone}</span></p>
              <div className="pt-2">
                <span
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${getStatusColor(
                    order.status
                  )}`}
                >
                  <StatusIcon className="h-5 w-5" />
                  <span className="font-semibold capitalize">{order.status}</span>
                </span>
              </div>
            </div>
          </div>

          {/* Shipping Address */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Shipping Address
            </h3>
            <div className="text-sm space-y-1">
              <p className="font-medium">{order.shippingAddress?.fullName}</p>
              <p>{order.shippingAddress?.addressLine1}</p>
              {order.shippingAddress?.addressLine2 && <p>{order.shippingAddress.addressLine2}</p>}
              {order.shippingAddress?.district && <p>{order.shippingAddress.district}</p>}
              <p>
                {order.shippingAddress?.city}, {order.shippingAddress?.state}{' '}
                {order.shippingAddress?.zipCode}
              </p>
              <p>{order.shippingAddress?.country}</p>
            </div>
          </div>

          {/* Payment Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Payment Information
            </h3>
            <div className="text-sm space-y-2">
              <p className="font-medium capitalize">{order.paymentMethod}</p>
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-semibold">
                <CheckCircle className="w-4 h-4" />
                {order.paymentStatus}
              </div>
              {order.paymentStatus === 'paid' && (
                <div className="pt-3 space-y-2">
                  {!confirmRefund ? (
                    <Button variant="outline" onClick={() => setConfirmRefund(true)} data-testid="refund-button">
                      Refund payment
                    </Button>
                  ) : (
                    <div className="border border-red-200 bg-red-50 rounded-md p-3 space-y-2">
                      <p className="text-red-800">
                        Refund the full {order.total} {order.currency} to the customer's card on Stripe? They receive
                        the refund e-mail. This can't be undone.
                      </p>
                      <div className="flex gap-2">
                        <Button onClick={handleRefund} disabled={refunding} className="bg-[#be1f1a] hover:bg-[#a71b17]">
                          {refunding ? 'Refunding…' : 'Yes, refund'}
                        </Button>
                        <Button variant="outline" onClick={() => setConfirmRefund(false)} disabled={refunding}>Cancel</Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOrderDetail;